import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { XAPIEventlog, XAPIVerb } from '@chancenwerk/db/eventlogs/XAPIEventlog.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/fonts.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { defineElements } from '@lumieducation/h5p-webcomponents';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { switchMap } from 'rxjs';
import { contentService } from '../../globals.js';

defineElements('h5p-player');

@customElement('campus-component-exercise-h5p')
export class CampusComponentExerciseH5P extends ChemistryLitElement {
  // States
  @state() public exerciseID = new RXController<string>(this, '');
  @state() public lessonID = new RXController<string>(this, '');

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _content = new RXController(
    this,
    this.exerciseID.observable.pipe(
      switchMap(contentID =>
        CampusExercise.subscribeByID<CampusExercise>(contentID)({ _class: 1, _id: 1, h5pContentID: 1 }),
      ),
    ),
  );

  // Declarations
  private h5pXAPIEventGuard = false;

  override render() {
    if (!this._content.value) return html`<cr-campus-loader></cr-campus-loader>`;
    return html`
      <cr-card ${css({ width: '100%', background: '#ffffff' })}>
        <cr-text>
          <h5p-player
            content-id=${this._content.value.h5pContentID}
            .loadContentCallback=${(contentId: string) => contentService.getPlay(contentId)}
            @initialized=${test => console.warn('initialized', test)}
            @xAPI=${({ detail: { statement } }) => {
              // This prevents multiple of these eventlisteners from firing.
              // Yes there will be as many listeners as times the player was attached to the dom, all of them run and i dont know why.
              if (this.h5pXAPIEventGuard) return;
              this.h5pXAPIEventGuard = true;
              setTimeout(async () => {
                this.h5pXAPIEventGuard = false;
                // filter out everything but answers
                // There are two more events you could track:
                // - Attemped: fired when the content is loaded
                // - Interacted: fired when an answer is selected
                // Answered is fired once you click the "Check" button

                //! TODO: CoursePresentation are currenty marked as completed when the user loads the content, not when he finishes it.
                // Alternative for this would be to somehow get the total amount of slides and check the progress on XAPIVerb.PROGRESSED
                if (
                  statement.verb.id === XAPIVerb.ATTEMPTED &&
                  this._content.value?.h5pContentDesc === 'H5P.CoursePresentation'
                )
                  return new XAPIEventlog({
                    userID: this._currentUser.value?._id,
                    lessonID: this.lessonID.value || 'LESSON_ID_NOT_PASSED',
                    contentID: this._content.value._id || 'INVALID_EXERCISE_ID',
                    h5pContentID: this._content.value.h5pContentID || 'INVALID_EXERCISE_h5pContentID',
                    verb: statement.verb.id,
                    result: true,
                  }).create();

                if (statement.verb.id !== XAPIVerb.ANSWERED) return;
                await new XAPIEventlog({
                  userID: this._currentUser.value?._id,
                  lessonID: this.lessonID.value || 'LESSON_ID_NOT_PASSED',
                  contentID: this._content.value?._id || 'INVALID_EXERCISE_ID',
                  h5pContentID: this._content.value?.h5pContentID || 'INVALID_EXERCISE_h5pContentID',
                  verb: statement.verb.id,
                  XApiResult: statement.result, // only set if verb === XAPIVerb.ANSWERED
                  result: statement.result?.success, // only set if verb === XAPIVerb.ANSWERED
                }).create();
              }, 0);
            }}
          ></h5p-player>
        </cr-text>
      </cr-card>
    `;
  }
}
